import { type FormEvent, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { useAuth } from '@/hooks/useAuth';
import styles from './RegisterPage.module.css';

type PasswordStrength = 'weak' | 'medium' | 'strong';

interface RegisterFormErrors {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const INITIAL_ERRORS: RegisterFormErrors = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
};

/**
 * Evaluates password strength based on length, character diversity, and complexity.
 * Returns 'weak', 'medium', or 'strong'.
 */
function evaluatePasswordStrength(password: string): PasswordStrength {
  if (!password) return 'weak';

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 2) return 'weak';
  if (score <= 3) return 'medium';
  return 'strong';
}

const STRENGTH_LABELS: Record<PasswordStrength, string> = {
  weak: 'Fraca',
  medium: 'Media',
  strong: 'Forte',
};

/**
 * Registration page with form validation and password strength indicator.
 *
 * Features:
 * - Client-side validation: email format, password min 8 chars, password match
 * - Visual password strength bar (weak/medium/strong)
 * - Loading state during API call
 * - Redirect to /login with success message on completion
 * - Focus management: auto-focus name field on mount
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const { register, isLoading, error: authError, clearError } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<RegisterFormErrors>(INITIAL_ERRORS);
  const [formError, setFormError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const passwordStrength = evaluatePasswordStrength(password);

  function handleFieldChange(field: keyof RegisterFormErrors, value: string) {
    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
    }

    if (formError) setFormError(null);
    if (authError) clearError();
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function validate(): boolean {
    const newErrors: RegisterFormErrors = {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    };
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = 'Nome completo é obrigatório.';
      isValid = false;
    } else if (name.trim().length < 3) {
      newErrors.name = 'Nome deve ter pelo menos 3 caracteres.';
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = 'E-mail é obrigatório.';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Formato de e-mail inválido.';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Senha é obrigatória.';
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres.';
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória.';
      isValid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (!validate()) {
      return;
    }

    try {
      await register({
        fullName: name.trim(),
        email: email.trim(),
        password,
      });
      navigate('/login', { state: { registered: true }, replace: true });
    } catch {
      setFormError(authError ?? 'Erro ao criar conta. Tente novamente.');
    }
  }

  const displayError = formError ?? authError;

  return (
    <AuthLayout>
      <div className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            CRM<span className={styles.titleAccent}>Clinica</span>
          </h1>
          <p className={styles.subtitle}>Crie sua conta</p>
        </header>

        {displayError && (
          <div className={styles.errorBanner} role="alert">
            <p>{displayError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className={styles.form}>
          <Input
            ref={nameRef}
            label="Nome Completo"
            type="text"
            name="name"
            placeholder="Seu nome completo"
            autoComplete="name"
            value={name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            error={errors.name}
            leftIcon={<User size={18} />}
            fullWidth
            required
          />

          <Input
            label="Email"
            type="email"
            name="email"
            placeholder="seu@email.com"
            autoComplete="email"
            value={email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            error={errors.email}
            leftIcon={<Mail size={18} />}
            fullWidth
            required
          />

          <div>
            <Input
              label="Senha"
              type="password"
              name="password"
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              value={password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              error={errors.password}
              leftIcon={<Lock size={18} />}
              fullWidth
              required
            />
            {password.length > 0 && (
              <div className={styles.strengthContainer}>
                <div
                  className={`${styles.strengthBar} ${styles[passwordStrength]}`}
                  role="progressbar"
                  aria-valuenow={passwordStrength === 'weak' ? 33 : passwordStrength === 'medium' ? 66 : 100}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Força da senha: ${STRENGTH_LABELS[passwordStrength]}`}
                >
                  <div className={styles.strengthFill} />
                </div>
                <span className={`${styles.strengthLabel} ${styles[passwordStrength]}`}>
                  {STRENGTH_LABELS[passwordStrength]}
                </span>
              </div>
            )}
          </div>

          <Input
            label="Confirmar Senha"
            type="password"
            name="confirmPassword"
            placeholder="Repita sua senha"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
            error={errors.confirmPassword}
            leftIcon={<Lock size={18} />}
            fullWidth
            required
          />

          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isLoading}>
            Criar conta
          </Button>
        </form>

        <p className={styles.loginLink}>
          Já tem conta?{' '}
          <Link to="/login">Faça login</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
